import { loadNutritionData, saveNutritionDataToStorage } from '@/lib/data/nutritionStorage'
import { MultiDayNutritionParser } from '@/lib/parsers/MultiDayNutritionParser'
import {
  addDaysToDate,
  areDatesEqual,
  compareDatesDesc,
  createDate,
  endOfDay,
  endOfWeek,
  startOfDay,
  subtractDays,
} from '@/lib/utils/date'
import { getWeekStart } from '@/lib/utils/weeklyNutritionUtils'
import { logger } from '@/services/logger'
import { DailyNutrition } from '@/types/nutrition'
import { create } from 'zustand'

/**
 * View mode for week navigation
 * - 'rolling': Shows the last 7 days (rolling window ending yesterday or latest data date)
 * - 'fixed': Shows Monday-Sunday weeks with navigation
 */
export type WeekViewMode = 'rolling' | 'fixed'

/**
 * Loading state for nutrition data
 */
export type LoadingState = 'idle' | 'loading' | 'success' | 'error'

/**
 * Nutrition store state interface
 */
export interface NutritionState {
  // Data state
  nutritionData: DailyNutrition[]
  loadingState: LoadingState
  error: Error | null

  // Week navigation state
  currentWeekStart: Date
  currentWeekEnd: Date
  viewMode: WeekViewMode
  rollingOffset: number // Days offset from current date for rolling mode (0 = yesterday, 7 = 7 days ago, etc.)

  // Computed state cache (for performance)
  isCurrentWeek: boolean
  isDataLoaded: boolean
  canNavigateNext: boolean // Whether navigation to next period is possible (has data)

  // Actions
  loadData: () => void
  loadDefaultData: () => Promise<void>
  setNutritionData: (data: DailyNutrition[]) => void
  addNutritionEntry: (entry: DailyNutrition) => void
  clearData: () => void

  // Week navigation actions
  setWeekStart: (date: Date) => void
  setViewMode: (mode: WeekViewMode) => void
  navigatePreviousWeek: () => boolean
  navigateNextWeek: () => boolean
  navigateToCurrentWeek: () => void
  navigateToLastWeek: () => void
  navigateToRolling: () => void
  toggleViewMode: () => void

  // Utility methods
  recalculateWeekDates: () => void
  resetError: () => void
}

/**
 * Check if there's any nutrition data within a date range
 * @param nutritionData - Array of daily nutrition data
 * @param startDate - Start of the date range (inclusive)
 * @param endDate - End of the date range (inclusive)
 * @returns True if at least one data entry exists in the range
 */
const hasDataInRange = (
  nutritionData: readonly DailyNutrition[] | unknown[] | undefined,
  startDate: Date,
  endDate: Date
): boolean => {
  if (!isDailyNutritionArray(nutritionData) || nutritionData.length === 0) {
    return false
  }

  const start = startOfDay(startDate)
  const end = endOfDay(endDate)

  return nutritionData.some((entry) => {
    const entryDate = startOfDay(entry.date)
    return entryDate >= start && entryDate <= end
  })
}

/**
 * Type guard to check if data is DailyNutrition array
 * @param data - Data to check
 * @returns True if data is DailyNutrition array
 */
const isDailyNutritionArray = (
  data: readonly DailyNutrition[] | unknown[] | undefined
): data is readonly DailyNutrition[] => {
  if (data === undefined || data.length === 0) return false
  const first = data[0]
  return (
    typeof first === 'object' &&
    first !== null &&
    'date' in first &&
    first.date instanceof Date &&
    'meals' in first &&
    'totals' in first
  )
}

/**
 * Find the most recent date with data in the nutrition data array
 * @param nutritionData - Array of daily nutrition data
 * @returns Most recent date with data, or null if no data
 */
const findMostRecentDataDate = (
  nutritionData: readonly DailyNutrition[] | unknown[] | undefined
): Date | null => {
  if (!isDailyNutritionArray(nutritionData) || nutritionData.length === 0) {
    return null
  }

  // Find the most recent date (assuming data might not be sorted)
  // Safe to use [0] here because we checked length > 0 above
  const firstDate = nutritionData[0]?.date
  if (!firstDate) {
    return null
  }

  const mostRecent = nutritionData.reduce((latest, entry) => {
    return entry.date > latest ? entry.date : latest
  }, firstDate)

  return mostRecent
}

/**
 * Get the end date for rolling mode (data-aware)
 * Returns yesterday (with offset) if no data provided or if data exists up to that date.
 * Returns most recent data date if it's before the target date.
 * @param nutritionData - Optional nutrition data array
 * @param offset - Days offset from current date (0 = yesterday, 7 = 7 days ago, etc.)
 * @returns Date representing the rolling end (end of day)
 */
const getRollingEnd = (nutritionData?: readonly DailyNutrition[] | unknown[], offset = 0): Date => {
  // Target date is yesterday minus the offset
  const targetDate = endOfDay(subtractDays(createDate(), 1 + offset))

  // If no nutrition data provided, use target date
  if (!nutritionData) {
    return targetDate
  }

  // Find the most recent date with data
  const mostRecentDate = findMostRecentDataDate(nutritionData)
  if (!mostRecentDate) {
    // No data at all - use target date
    return targetDate
  }

  // If most recent data is on or after the target date, use target date
  // This handles the case where data is current for the given offset
  const targetStartOfDay = startOfDay(subtractDays(createDate(), 1 + offset))
  if (mostRecentDate >= targetStartOfDay) {
    return targetDate
  }

  // Most recent data is before target date - use that date as the end
  return endOfDay(mostRecentDate)
}

/**
 * Get the start date for rolling mode (last 7 days)
 * If nutritionData is provided and no data exists in the default range,
 * adjusts to show last 7 days from most recent data date.
 * @param nutritionData - Optional nutrition data array
 * @param offset - Days offset from current date (0 = yesterday, 7 = 7 days ago, etc.)
 * @returns Date representing the rolling start
 */
const getRollingStart = (
  nutritionData?: readonly DailyNutrition[] | unknown[],
  offset = 0
): Date => {
  // Get the data-aware end date with offset
  const rollingEnd = getRollingEnd(nutritionData, offset)

  // Calculate start as 6 days before the end (to create a 7-day range)
  return startOfDay(subtractDays(rollingEnd, 6))
}

/**
 * Check if the given week start is the actual current week
 * @param weekStart - Week start date to check
 * @returns True if it's the current week
 */
const checkIsCurrentWeek = (weekStart: Date): boolean => {
  const actualCurrentWeekStart = getWeekStart(createDate())
  return areDatesEqual(weekStart, actualCurrentWeekStart)
}

/**
 * Check if navigation to next period is possible
 * @param viewMode - Current view mode
 * @param rollingOffset - Current rolling offset
 * @param nutritionData - Nutrition data array
 * @returns True if next navigation is possible
 */
const checkCanNavigateNext = (
  viewMode: WeekViewMode,
  rollingOffset: number,
  nutritionData: readonly DailyNutrition[] | unknown[] | undefined
): boolean => {
  if (viewMode === 'fixed') {
    // In fixed mode, always allow next (can go into future)
    return true
  }

  // In rolling mode, check if we're at offset 0 (most recent) or if there's data in the next period
  if (rollingOffset === 0) {
    return false // Already at most recent
  }

  // Check if there's data in the next period (offset - 7)
  const nextOffset = Math.max(0, rollingOffset - 7)
  const nextEnd = getRollingEnd(nutritionData, nextOffset)
  const nextStart = getRollingStart(nutritionData, nextOffset)

  return hasDataInRange(nutritionData, nextStart, nextEnd)
}

/**
 * Zustand store for nutrition data and week navigation state
 * Provides centralized state management with localStorage persistence
 */
export const useNutritionStore = create<NutritionState>((set, get) => ({
  // Initial state
  nutritionData: [],
  loadingState: 'idle',
  error: null,
  currentWeekStart: getRollingStart(),
  currentWeekEnd: getRollingEnd(),
  viewMode: 'rolling',
  rollingOffset: 0,
  isCurrentWeek: false,
  isDataLoaded: false,
  canNavigateNext: false,

  /**
   * Load nutrition data from localStorage
   * Updates loading state and recalculates week dates
   */
  loadData: () => {
    set({ loadingState: 'loading', error: null })

    try {
      const data = loadNutritionData()
      const { viewMode, rollingOffset } = get()
      set({
        nutritionData: data,
        loadingState: 'success',
        isDataLoaded: true,
        canNavigateNext: checkCanNavigateNext(viewMode, rollingOffset, data),
      })

      // Recalculate week dates based on loaded data
      get().recalculateWeekDates()

      logger.debug('Nutrition data loaded successfully', { count: data.length })
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error loading nutrition data')
      logger.error('Failed to load nutrition data', err)
      set({
        loadingState: 'error',
        error: err,
      })
    }
  },

  /**
   * Load default nutrition data from public assets
   * Fetches data from /data/default-nutrition-data.md, parses it, and merges with existing data
   * Automatically saves to localStorage and updates the store
   * @throws Error if fetching or parsing fails
   */
  loadDefaultData: async () => {
    set({ loadingState: 'loading', error: null })

    try {
      // Fetch the default nutrition data from public assets
      const response = await fetch('/data/default-nutrition-data.md')
      if (!response.ok) {
        throw new Error(
          `Failed to load default data: ${response.status} ${response.statusText}`.trim()
        )
      }

      const defaultDataContent = await response.text()

      // Parse the multi-day nutrition data
      const parser = new MultiDayNutritionParser()
      const result = parser.parseMultiDay(defaultDataContent)

      if (!result.success) {
        throw new Error(result.error.message)
      }

      // Merge with existing data
      const { nutritionData } = get()
      const newData = [...nutritionData, ...result.data]

      // Save to localStorage
      saveNutritionDataToStorage(newData)

      // Update store state
      set({
        nutritionData: newData,
        loadingState: 'success',
        isDataLoaded: true,
      })

      // Recalculate week dates based on new data
      get().recalculateWeekDates()

      logger.info(`Successfully loaded ${result.data.length} days of default data`)
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error loading default data')
      logger.error('Error loading default data', err)
      set({
        loadingState: 'error',
        error: err,
      })
      throw err
    }
  },

  /**
   * Set nutrition data directly (for testing or external updates)
   * @param data - Array of DailyNutrition entries
   */
  setNutritionData: (data) => {
    const { viewMode, rollingOffset } = get()
    set({
      nutritionData: data,
      loadingState: 'success',
      isDataLoaded: true,
      canNavigateNext: checkCanNavigateNext(viewMode, rollingOffset, data),
    })
    get().recalculateWeekDates()
  },

  /**
   * Add or update a nutrition entry
   * Automatically recalculates week dates if needed
   * @param entry - DailyNutrition entry to add
   */
  addNutritionEntry: (entry) => {
    const { nutritionData } = get()

    // Check if entry for this date already exists
    const existingIndex = nutritionData.findIndex(
      (existing) => existing.date.toDateString() === entry.date.toDateString()
    )

    let updated: DailyNutrition[]
    if (existingIndex >= 0) {
      // Replace existing entry
      updated = [...nutritionData]
      updated[existingIndex] = entry
    } else {
      // Add new entry and sort by date (most recent first)
      updated = [...nutritionData, entry].sort((a, b) => compareDatesDesc(a.date, b.date))
    }

    set({ nutritionData: updated })
    get().recalculateWeekDates()
  },

  /**
   * Clear all nutrition data from store and localStorage
   */
  clearData: () => {
    // Clear localStorage
    localStorage.removeItem('strive_nutrition_data')

    // Clear store state
    set({
      nutritionData: [],
      loadingState: 'idle',
      error: null,
      isDataLoaded: false,
    })
    get().recalculateWeekDates()
  },

  /**
   * Set week start date (for fixed mode)
   * @param date - New week start date
   */
  setWeekStart: (date) => {
    const { viewMode, nutritionData } = get()

    set({
      currentWeekStart: date,
      currentWeekEnd: viewMode === 'rolling' ? getRollingEnd(nutritionData) : endOfWeek(date),
      isCurrentWeek: viewMode === 'fixed' && checkIsCurrentWeek(date),
    })
  },

  /**
   * Set view mode (rolling or fixed)
   * @param mode - New view mode
   */
  setViewMode: (mode) => {
    const { nutritionData, currentWeekStart } = get()

    if (mode === 'rolling') {
      const rollingEnd = getRollingEnd(nutritionData, 0)
      const rollingStart = getRollingStart(nutritionData, 0)

      set({
        viewMode: mode,
        rollingOffset: 0,
        currentWeekStart: rollingStart,
        currentWeekEnd: rollingEnd,
        isCurrentWeek: false,
        canNavigateNext: false,
      })
    } else {
      set({
        viewMode: mode,
        currentWeekEnd: endOfWeek(currentWeekStart),
        isCurrentWeek: checkIsCurrentWeek(currentWeekStart),
        canNavigateNext: true,
      })
    }
  },

  /**
   * Navigate to the previous week/period
   * - In fixed mode: Navigate to previous Monday-Sunday week
   * - In rolling mode: Navigate to previous 7-day period
   * @returns boolean - true if navigation occurred
   */
  navigatePreviousWeek: () => {
    const { viewMode, currentWeekStart, rollingOffset, nutritionData } = get()

    if (viewMode === 'rolling') {
      // In rolling mode, increment offset by 7 days
      const newOffset = rollingOffset + 7
      const newEnd = getRollingEnd(nutritionData, newOffset)
      const newStart = getRollingStart(nutritionData, newOffset)

      set({
        rollingOffset: newOffset,
        currentWeekStart: newStart,
        currentWeekEnd: newEnd,
        canNavigateNext: checkCanNavigateNext('rolling', newOffset, nutritionData),
      })
      return true
    }

    // Fixed mode
    const newStart = subtractDays(currentWeekStart, 7)
    set({
      currentWeekStart: newStart,
      currentWeekEnd: endOfWeek(newStart),
      isCurrentWeek: checkIsCurrentWeek(newStart),
      canNavigateNext: true, // Always true in fixed mode
    })
    return true
  } /**
   * Navigate to the next week/period
   * - In fixed mode: Navigate to next Monday-Sunday week
   * - In rolling mode: Navigate to next 7-day period (max offset 0 = most recent)
   * @returns boolean - true if navigation occurred
   */,
  navigateNextWeek: () => {
    const { viewMode, currentWeekStart, rollingOffset, nutritionData } = get()

    if (viewMode === 'rolling') {
      // In rolling mode, decrement offset by 7 days (but not below 0)
      if (rollingOffset === 0) {
        // Already at most recent period
        return false
      }

      const newOffset = Math.max(0, rollingOffset - 7)
      const newEnd = getRollingEnd(nutritionData, newOffset)
      const newStart = getRollingStart(nutritionData, newOffset)

      set({
        rollingOffset: newOffset,
        currentWeekStart: newStart,
        currentWeekEnd: newEnd,
        canNavigateNext: checkCanNavigateNext('rolling', newOffset, nutritionData),
      })
      return true
    }

    // Fixed mode
    const newStart = addDaysToDate(currentWeekStart, 7)
    set({
      currentWeekStart: newStart,
      currentWeekEnd: endOfWeek(newStart),
      isCurrentWeek: checkIsCurrentWeek(newStart),
      canNavigateNext: true, // Always true in fixed mode
    })
    return true
  },

  /**
   * Navigate to current week (Monday-Sunday) and switch to fixed mode
   */
  navigateToCurrentWeek: () => {
    const currentWeekStart = getWeekStart(createDate())
    set({
      viewMode: 'fixed',
      rollingOffset: 0,
      currentWeekStart,
      currentWeekEnd: endOfWeek(currentWeekStart),
      isCurrentWeek: true,
      canNavigateNext: true,
    })
  },

  /**
   * Navigate to last week (previous Monday-Sunday) and switch to fixed mode
   */
  navigateToLastWeek: () => {
    const currentWeekStart = getWeekStart(createDate())
    const lastWeekStart = subtractDays(currentWeekStart, 7)

    set({
      viewMode: 'fixed',
      currentWeekStart: lastWeekStart,
      currentWeekEnd: endOfWeek(lastWeekStart),
      isCurrentWeek: false,
      canNavigateNext: true,
    })
  },

  /**
   * Navigate to rolling mode (last 7 days)
   */
  navigateToRolling: () => {
    const { nutritionData } = get()
    const newEnd = getRollingEnd(nutritionData, 0)
    const newStart = getRollingStart(nutritionData, 0)

    set({
      viewMode: 'rolling',
      rollingOffset: 0,
      currentWeekStart: newStart,
      currentWeekEnd: newEnd,
      canNavigateNext: false, // At offset 0, can't go forward
    })
  },

  /**
   * Toggle between rolling and fixed modes
   * - From rolling → fixed: Switches to current week
   * - From fixed → rolling: Switches to last 7 days
   */
  toggleViewMode: () => {
    const { viewMode } = get()

    if (viewMode === 'rolling') {
      get().navigateToCurrentWeek()
    } else {
      get().navigateToRolling()
    }
  },

  /**
   * Recalculate week dates based on current state
   * Used when nutrition data changes and we're in rolling mode
   */
  recalculateWeekDates: () => {
    const { viewMode, currentWeekStart, nutritionData, rollingOffset, isCurrentWeek } = get()

    if (viewMode === 'rolling') {
      // Recalculate rolling dates with data-aware logic and current offset
      const newEnd = getRollingEnd(nutritionData, rollingOffset)
      const newStart = getRollingStart(nutritionData, rollingOffset)

      // Only update if dates actually changed
      const currentEnd = get().currentWeekEnd
      const currentStart = get().currentWeekStart

      if (
        newStart.getTime() !== currentStart.getTime() ||
        newEnd.getTime() !== currentEnd.getTime()
      ) {
        set({
          currentWeekStart: newStart,
          currentWeekEnd: newEnd,
        })
      }
    } else {
      // In fixed mode, update isCurrentWeek flag only if it changed
      const newIsCurrentWeek = checkIsCurrentWeek(currentWeekStart)
      if (newIsCurrentWeek !== isCurrentWeek) {
        set({
          isCurrentWeek: newIsCurrentWeek,
        })
      }
    }
  },

  /**
   * Reset error state
   */
  resetError: () => {
    set({ error: null })
  },
}))

/**
 * Selector for nutrition data only (optimized for re-renders)
 */
export const selectNutritionData = (state: NutritionState): DailyNutrition[] => state.nutritionData

/**
 * Selector for loading state
 */
export const selectLoadingState = (state: NutritionState): LoadingState => state.loadingState

/**
 * Selector for week navigation state
 * Returns individual properties to avoid creating new objects on every call
 */
export const selectCurrentWeekStart = (state: NutritionState): Date => state.currentWeekStart
export const selectCurrentWeekEnd = (state: NutritionState): Date => state.currentWeekEnd
export const selectViewMode = (state: NutritionState): WeekViewMode => state.viewMode
export const selectIsCurrentWeek = (state: NutritionState): boolean => state.isCurrentWeek

/**
 * Selector for data loaded state
 */
export const selectIsDataLoaded = (state: NutritionState): boolean => state.isDataLoaded

/**
 * Selector for canNavigateNext state
 */
export const selectCanNavigateNext = (state: NutritionState): boolean => state.canNavigateNext
